import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useColorScheme } from "../../context/ColorSchemeContext";
import { useSessionTimer } from "../../hooks/useSessionTimer";
import { ThemeBadge } from "../theme/ThemeBadge";
import { SubscriptionBadge } from "../theme/SubscriptionBadge";
import { Button } from "../common/Button";
import { Toggle } from "../common/Toggle";
import styles from "./TopBar.module.css";
import { AccountSwitcher } from "../common/AccountSwitcher";

const LANGUAGES = { en: "EN", es: "ES", fr: "FR" };

export function TopBar({ onMenuToggle }) {
  const { logout, stayLoggedIn, setStayLoggedIn } = useAuth();
  const { isDark, toggle: toggleDark } = useColorScheme();
  const { label: sessionLabel } = useSessionTimer();
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className={styles.bar}>
      <button className={[styles.iconButton, styles.menuButton].join(" ")} onClick={onMenuToggle} aria-label="Toggle navigation menu">
        ☰
      </button>

      <a href="/" className={styles.brand}>🐝 Bee Keeper</a>

      <div className={styles.badges}>
        <ThemeBadge />
        <SubscriptionBadge />
        {/* Beside the tier badges rather than in a menu: it decides which
            apiary everything you create belongs to, so it has to be
            readable before you start, not found afterwards (#310). Renders
            nothing at all unless you actually help at another apiary. */}
        <AccountSwitcher />
      </div>

      <div className={styles.spacer} />

      <div className={styles.sessionInfo} title="Time remaining in this session">
        <span aria-hidden="true">⏱</span>
        <span>{sessionLabel}</span>
      </div>

      <div className={styles.stayLoggedIn}>
        <Toggle checked={stayLoggedIn} onChange={setStayLoggedIn} label="Stay logged in" id="stay-logged-in-topbar" />
      </div>

      {/* id AND name, not just aria-label. The label makes it
          accessible; the id/name is what stops Chrome reporting "a form
          field element has neither an id nor a name attribute" and lets
          the browser treat it as a real field. Reported from dev,
          4 Oct. */}
      <select
        id="language-select"
        name="language"
        aria-label="Language"
        className={styles.langSelect}
        value={i18n.language?.slice(0, 2)}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
      >
        {Object.entries(LANGUAGES).map(([code, label]) => (
          <option key={code} value={code}>{label}</option>
        ))}
      </select>

      <button className={styles.iconButton} onClick={toggleDark} aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}>
        {isDark ? "☀️" : "🌙"}
      </button>

      <div className={styles.userMenu}>
        <button className={styles.iconButton} onClick={() => setMenuOpen((v) => !v)} aria-haspopup="true" aria-expanded={menuOpen}>
          👤
        </button>
        {menuOpen && (
          <div className={styles.dropdown} role="menu">
            <a href="/settings" role="menuitem" className={styles.dropdownItem}>Settings</a>
            <a href="/settings/security" role="menuitem" className={styles.dropdownItem}>Security Center</a>
            <Button variant="ghost" size="sm" onClick={logout} className={styles.dropdownItem}>
              Sign out
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
